/**
 * 标准配合尺寸换算工具。
 *
 * 文件职责：
 * 1. 统一 SOLIDWORKS 风格 PropertyManager 数值到渲染空间的换算；
 * 2. 保证求解器与最终残差校验使用同一套线性尺寸语义；
 * 3. 避免 Distance 等配合在 UI 毫米值与 Three.js 场景单位之间出现隐式偏差。
 */

import { IMPORTED_BREP_MM_TO_RENDER_SPACE_SCALE } from '../../../imported-model/topology/imported-model-topology-space';
import { AssemblyMateType, type AssemblyMateDefinition } from '../../definition';

/** 标准配合默认线性尺寸换算比例：毫米输入到当前渲染空间。 */
export const STANDARD_MATE_DEFAULT_LINEAR_DIMENSION_SCALE = IMPORTED_BREP_MM_TO_RENDER_SPACE_SCALE;

/**
 * 解析标准配合线性尺寸换算比例。
 *
 * @param scale 外部注入的换算比例。
 * @returns 可用于求解的换算比例。
 */
export function resolveStandardMateLinearDimensionScale(scale: number | undefined): number {
  if (typeof scale === 'number' && Number.isFinite(scale) && scale > 0) {
    return scale;
  }
  return STANDARD_MATE_DEFAULT_LINEAR_DIMENSION_SCALE;
}

/**
 * 将 SOLIDWORKS 风格线性尺寸转换为场景单位。
 *
 * @param value PropertyManager 中的线性尺寸值。
 * @param scale 线性尺寸换算比例。
 * @returns 场景单位尺寸。
 */
export function convertStandardMateLinearDimensionToSceneUnit(
  value: number,
  scale: number,
): number {
  return value * resolveStandardMateLinearDimensionScale(scale);
}

/**
 * 读取 Mate 的第一选择到第二选择方向上的有符号距离。
 *
 * @param mate 配合定义。
 * @param linearDimensionScale 线性尺寸换算比例。
 * @returns 场景单位下的有符号距离。
 */
export function resolveStandardMateSignedDistance(
  mate: AssemblyMateDefinition,
  linearDimensionScale: number,
): number {
  if (mate.mateType !== AssemblyMateType.Distance) {
    return 0;
  }

  const sign = mate.dimension?.flipped ? -1 : 1;
  return convertStandardMateLinearDimensionToSceneUnit(
    sign * (mate.dimension?.value ?? 0),
    linearDimensionScale,
  );
}
